/**
 * Centralized Pricing Calculations Utility
 * 
 * Handles all pricing calculations for Patagonia Vital orders.
 * Supports dual-currency pricing:
 * - Exportación orders: USD using FOB MIA pricing
 * - Nacional orders: CLP using local pricing
 * 
 * Formulas:
 * - Exportación (USD): precio_fob_mia × ramos_por_caja × cajas
 * - Nacional (CLP): precio_unitario × ramos_por_caja × tallos_por_ramo × cajas
 */

/**
 * Calculate total for a single order item
 * @param {Object} producto - Product object with pricing and attributes
 * @param {string} orderType - 'Exportación' or 'Nacional'
 * @param {number} cajas - Number of boxes
 * @returns {number} Total amount in appropriate currency
 * @throws {Error} If validation fails or invalid order type
 */
export function calculateOrderItemTotal(producto, orderType, cajas) {
  if (!producto) {
    throw new Error('Product object is required');
  }

  if (!orderType || (orderType !== 'Exportación' && orderType !== 'Nacional')) {
    const error = new Error(`Invalid order type: ${orderType}. Must be 'Exportación' or 'Nacional'`);
    console.error(error.message);
    throw error;
  }

  const cajasNum = Number(cajas);
  if (isNaN(cajasNum) || cajasNum <= 0) {
    throw new Error('Cajas must be a positive number');
  }

  if (orderType === 'Exportación') {
    const precioFobMia = Number(producto.precio_fob_mia);
    const ramosPorCaja = Number(producto.ramos_por_caja || producto.bunches_box);

    if (isNaN(precioFobMia) || precioFobMia === null || precioFobMia === undefined) {
      throw new Error('precio_fob_mia is required for Exportación orders');
    }

    if (isNaN(ramosPorCaja) || ramosPorCaja <= 0) {
      throw new Error('ramos_por_caja must be greater than zero');
    }

    return precioFobMia * ramosPorCaja * cajasNum;
  } else {
    const precioUnitario = Number(producto.precio_unitario);
    const ramosPorCaja = Number(producto.ramos_por_caja || producto.bunches_box);
    const tallosPorRamo = Number(producto.tallos_por_ramo || producto.stems_bunches);

    if (isNaN(precioUnitario) || precioUnitario === null || precioUnitario === undefined) {
      throw new Error('precio_unitario is required for Nacional orders');
    }

    if (isNaN(ramosPorCaja) || ramosPorCaja <= 0) {
      throw new Error('ramos_por_caja must be greater than zero');
    }

    if (isNaN(tallosPorRamo) || tallosPorRamo <= 0) {
      throw new Error('tallos_por_ramo must be greater than zero');
    }

    return precioUnitario * ramosPorCaja * tallosPorRamo * cajasNum;
  }
}

/**
 * Calculate total for an entire order with multiple items
 * @param {Array} items - Array of order items with producto and cajas/cantidad_cajas
 * @param {string} orderType - 'Exportación' or 'Nacional'
 * @returns {Object} Object with total and currency { total: number, currency: string }
 */
export function calculateOrderTotal(items, orderType) {
  if (!Array.isArray(items)) {
    throw new Error('Items must be an array');
  }

  if (!orderType || (orderType !== 'Exportación' && orderType !== 'Nacional')) {
    throw new Error(`Invalid order type: ${orderType}. Must be 'Exportación' or 'Nacional'`);
  }

  const total = items.reduce((sum, item) => {
    const producto = item.producto || item;
    const cajas = item.cajas || item.cantidad_cajas || 0;
    
    try {
      return sum + calculateOrderItemTotal(producto, orderType, cajas);
    } catch (error) {
      console.error(`Error calculating item total for product ${producto.id || producto.nombre}:`, error);
      return sum;
    }
  }, 0);

  const currency = orderType === 'Exportación' ? 'USD' : 'CLP';

  return { total, currency };
}

/**
 * Format price for display based on currency
 * @param {number} amount - Numeric amount to format
 * @param {string} currency - 'CLP' or 'USD'
 * @returns {string} Formatted price string
 */
export function formatPrice(amount, currency) {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return currency === 'USD' ? '$0.00' : '$0';
  }

  const numAmount = Number(amount);

  if (currency === 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numAmount);
  } else if (currency === 'CLP') {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numAmount);
  } else {
    throw new Error(`Invalid currency: ${currency}. Must be 'CLP' or 'USD'`);
  }
}

/**
 * Get currency for order type
 * @param {string} orderType - 'Exportación' or 'Nacional'
 * @returns {string} 'USD' or 'CLP'
 */
export function getCurrency(orderType) {
  if (orderType === 'Exportación') {
    return 'USD';
  } else if (orderType === 'Nacional') {
    return 'CLP';
  } else {
    throw new Error(`Invalid order type: ${orderType}`);
  }
}

/**
 * Get price field name for order type
 * @param {string} orderType - 'Exportación' or 'Nacional'
 * @returns {string} 'precio_fob_mia' or 'precio_unitario'
 */
export function getPriceField(orderType) {
  if (orderType === 'Exportación') {
    return 'precio_fob_mia';
  } else if (orderType === 'Nacional') {
    return 'precio_unitario';
  } else {
    throw new Error(`Invalid order type: ${orderType}`);
  }
}

/**
 * Get price label for order type
 * @param {string} orderType - 'Exportación' or 'Nacional'
 * @returns {string} Price label with currency
 */
export function getPriceLabel(orderType) {
  if (orderType === 'Exportación') {
    return 'Precio FOB MIA (USD)';
  } else if (orderType === 'Nacional') {
    return 'Precio Nacional (CLP)';
  } else {
    throw new Error(`Invalid order type: ${orderType}`);
  }
}
